import { useState, useEffect } from "react";
import { Apple } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import '../styles/navbar.css';

const Navbar = ({ onOpenLogin }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Bug Fix: Prevent body scroll when menu is open to ensure clicks register correctly
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileOpen]);

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection(location.pathname === "/blogs" ? "blogs" : "");
      return;
    }

    const sectionIds = ["roles", "categories", "about", "faq"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [location.pathname]);

  const links = [
    { href: "#roles", label: "Features", id: "roles" },
    { href: "#categories", label: "Browse", id: "categories" },
    { href: "#about", label: "About Us", id: "about" },
    { href: "#faq", label: "FAQ", id: "faq" },
    { href: "/blogs", label: "Blogs", id: "blogs" },
  ];

  const renderNavLink = (link) => {
    const isActive = activeSection === link.id;
    const toPath = link.href.startsWith("/") ? link.href : `/${link.href}`;

    return (
      <Link
        key={link.label}
        to={toPath}
        className={isActive ? "active" : ""}
        onClick={() => {
          setMobileOpen(false);
          if (!link.href.startsWith("/")) {
            setActiveSection(link.id);
          }
        }}
      >
        {link.label}
      </Link>
    );
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
        <Link to="/" className="navbar-logo" onClick={() => { setMobileOpen(false); setActiveSection(""); }}>
          <img src="/images/modal logo.png" alt="ZamWheelz" className="navbar-logo-image" width="280" height="83" fetchPriority="high" />
        </Link>

        <div className="navbar-links">
          {links.map((l) => renderNavLink(l))}
        </div>

        <div className="navbar-actions">
          <div className="store-buttons-wrapper">
            <button type="button" className="store-action-btn store-action-apple">
         <svg viewBox="0 0 24 24" fill="black" width="28" height="28">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="btn-text-wrapper">
                <span className="btn-small-text">Download on the</span>
                <span className="btn-main-text">App Store</span>
              </div>
            </button>

            <button type="button" className="store-action-btn store-action-google">
              <svg viewBox="0 0 512 512" width="22" height="22">
                <path d="M7.9 22.2C5.1 25.5 3.5 30.1 3.5 35.8V476.3C3.5 482 5.1 486.5 7.9 489.9L10.2 492.2L262.2 240.2L262.2 235.8L10.2 19.8L7.9 22.2Z" fill="#3bccff" />
                <path d="M346.2 324.2L262.2 240.2L262.2 235.8L346.2 151.8L348.5 153.1L447.9 210.1C476.2 226.2 476.2 251.8 447.9 267.9L348.5 324.9L346.2 324.2Z" fill="#ffdb00" />
                <path d="M348.5 324.9L262.2 240.2L7.9 489.9C16.8 499.2 31.4 500.3 47.9 491.1L348.5 324.9" fill="#ff3b3b" />
                <path d="M348.5 153.1L47.9 22.9C31.4 13.7 16.8 14.8 7.9 24.1L262.2 235.8L348.5 153.1" fill="#48ff48" />
              </svg>
              <div className="btn-text-wrapper">
                <span className="btn-small-text">GET IT ON</span>
                <span className="btn-main-text">Google Play</span>
              </div>
            </button>
          </div>
          <div className="auth-group">
            <button className="btn-outline" onClick={onOpenLogin}>Login</button>
          </div>
        </div>

        <button className="mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle Menu">
          {mobileOpen ? "✕" : "☰"}
        </button>
      </nav>
      
      <div className={`mobile-nav-container ${mobileOpen ? "open" : ""}`}>
        <div className="mobile-links-wrapper">
          {links.map((l) => renderNavLink(l))}
        </div>
        <div className="store-buttons-wrapper mobile-store-buttons">
          <button type="button" className="store-action-btn store-action-apple">
            <Apple size={22} color="#000" fill="#000" />
            <div className="btn-text-wrapper">
              <span className="btn-small-text">Download on the</span>
              <span className="btn-main-text">App Store</span>
            </div>
          </button>
          <button type="button" className="store-action-btn store-action-google">
            <svg viewBox="0 0 512 512" width="22" height="22">
              <path d="M7.9 22.2C5.1 25.5 3.5 30.1 3.5 35.8V476.3C3.5 482 5.1 486.5 7.9 489.9L10.2 492.2L262.2 240.2L262.2 235.8L10.2 19.8L7.9 22.2Z" fill="#3bccff" />
              <path d="M346.2 324.2L262.2 240.2L262.2 235.8L346.2 151.8L348.5 153.1L447.9 210.1C476.2 226.2 476.2 251.8 447.9 267.9L348.5 324.9L346.2 324.2Z" fill="#ffdb00" />
              <path d="M348.5 324.9L262.2 240.2L7.9 489.9C16.8 499.2 31.4 500.3 47.9 491.1L348.5 324.9" fill="#ff3b3b" />
              <path d="M348.5 153.1L47.9 22.9C31.4 13.7 16.8 14.8 7.9 24.1L262.2 235.8L348.5 153.1" fill="#48ff48" />
            </svg>
            <div className="btn-text-wrapper">
              <span className="btn-small-text">GET IT ON</span>
              <span className="btn-main-text">Google Play</span>
            </div>
          </button>
        </div>
        <div className="mobile-auth-section">
          <button className="mobile-auth-btn" onClick={() => { onOpenLogin(); setMobileOpen(false); }}>Login</button>
        </div>
      </div>
    </>
  );
};

export default Navbar;